import React from "react";
import { translate } from "../../translate/translate";

export interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  style,
  width,
  height,
  borderRadius = 4,
  ...props
}) => {
  return (
    <div
      className={className ? `animate-pulse ${className}` : "animate-pulse"}
      style={{
        backgroundColor: "#e5e7eb",
        borderRadius,
        width,
        height,
        ...style,
      }}
      {...props}
    />
  );
};

export const PaginationSkeleton: React.FC = () => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "16px",
        padding: "12px 20px",
        borderTop: "1px solid #f3f4f6",
      }}
    >
      <Skeleton width={112} height={16} />
      <Skeleton width={48} height={24} />
      <Skeleton width={96} height={16} />
      <Skeleton width={56} height={24} />
    </div>
  );
};

/* ── Items and Demand ────────────────────────────────────────────────────── */

export const DemandItemsKpiSkeleton: React.FC = () => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        backgroundColor: "#ffffff",
        padding: "10px 16px",
        flex: 1,
        minWidth: "130px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Skeleton width={56} height={28} />
        <Skeleton style={{ marginTop: 4, width: "100%", maxWidth: 160, height: 12 }} />
      </div>
    </div>
  );
};

const TEXT_WIDTHS = ["75%", "100%", "66%", "83%"];

export const DEFAULT_ID_COLUMNS = [
  { label: "Part Number", w: 150 },
  { label: "Description", w: 200 },
  { label: "Material Type", w: 105 },
  { label: "Models", w: 150 },
  { label: "Forecast", w: 95, num: true },
  { label: "Stock", w: 90, num: true },
  { label: "In Transit", w: 95, num: true },
  { label: "Pending Invoice", w: 135, num: true },
  { label: "Balance", w: 95, num: true },
  { label: "Coverage (Weeks)", w: 145, center: true },
];

export const DemandItemsCardSkeleton: React.FC<{
  columns?: { label?: string; w: number; num?: boolean; center?: boolean }[];
  rows?: number;
}> = ({ columns = DEFAULT_ID_COLUMNS, rows = 10 }) => {
  const minWidth = columns.reduce((sum, c) => sum + c.w, 0);

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        boxSizing: "border-box",
        marginBottom: "24px",
      }}
      aria-busy="true"
      aria-label={translate.t("materialStatus.loading.demands", "Loading items and demand")}
    >
      {/* Top filters bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "20px 20px 16px 20px",
          borderBottom: "1px solid #f3f4f6",
          flexWrap: "wrap",
        }}
      >
        <Skeleton style={{ height: 38, flex: 1, minWidth: 180, borderRadius: 8 }} />
        {[140, 120, 200, 120].map((w, i) => (
          <Skeleton key={i} style={{ height: 38, width: w, flexShrink: 0, borderRadius: 8 }} />
        ))}
        <Skeleton style={{ height: 36, width: 36, borderRadius: "50%", flexShrink: 0 }} />
        <Skeleton style={{ height: 38, width: 150, borderRadius: 8, flexShrink: 0 }} />
        <Skeleton style={{ height: 38, width: 130, borderRadius: 8, flexShrink: 0 }} />
      </div>

      {/* Subheader bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 20px",
          borderBottom: "1px solid #f3f4f6",
          backgroundColor: "rgba(249, 250, 251, 0.6)",
        }}
      >
        <Skeleton style={{ height: 14, width: 14, borderRadius: "50%" }} />
        <Skeleton style={{ height: 12, width: 288 }} />
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: "14px", minWidth, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#f3f4f6" }}>
              {columns.map((c, i) => (
                <th
                  key={i}
                  style={{
                    padding: "14px 12px",
                    width: c.w,
                    minWidth: c.w,
                    textAlign: c.num ? "right" : c.center ? "center" : "left",
                  }}
                >
                  <Skeleton
                    style={{
                      height: 12,
                      width: "66%",
                      marginLeft: c.num ? "auto" : undefined,
                      marginRight: c.center ? "auto" : undefined,
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, l) => (
              <tr key={l} style={{ borderBottom: "1px solid #f3f4f6" }}>
                {columns.map((c, i) => (
                  <td key={i} style={{ padding: "14px 12px" }}>
                    <Skeleton
                      style={{
                        height: c.center ? 20 : 12,
                        width: c.num ? 48 : c.center ? 64 : TEXT_WIDTHS[(l + i) % TEXT_WIDTHS.length],
                        borderRadius: c.center ? 9999 : 4,
                        marginLeft: c.num ? "auto" : undefined,
                        marginRight: c.center ? "auto" : undefined,
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationSkeleton />
    </div>
  );
};

/* ── GDS Result ─────────────────────────────────────────────────────────── */

const GDS_TEXT_COLUMNS = [140, 200, 100, 160, 160];
const GDS_NUM_COLUMNS = [90, 90, 90, 90, 100];
const GDS_ROWS_PER_PN = 4;

export const GdsCardSkeleton: React.FC<{ partNumbers?: number }> = ({ partNumbers = 10 }) => {
  const minWidth = [...GDS_TEXT_COLUMNS, 36, ...GDS_NUM_COLUMNS].reduce((a, b) => a + b, 0);

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        boxSizing: "border-box",
        marginBottom: "24px",
      }}
      aria-busy="true"
      aria-label={translate.t("materialStatus.loading.gds", "Loading GDS result")}
    >
      {/* Top action bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "20px 20px 16px 20px",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <Skeleton style={{ height: 42, flex: 1, minWidth: 280, borderRadius: 8 }} />
        <Skeleton style={{ height: 38, width: 130, borderRadius: 8, flexShrink: 0 }} />
        <Skeleton style={{ height: 38, width: 95, borderRadius: 8, flexShrink: 0 }} />
        <Skeleton style={{ height: 38, width: 130, borderRadius: 8, flexShrink: 0 }} />
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse", minWidth }}>
          <thead>
            <tr style={{ backgroundColor: "#f3f4f6" }}>
              {GDS_TEXT_COLUMNS.map((w, i) => (
                <th
                  key={i}
                  style={{
                    padding: "16px 12px",
                    borderRight: "1px solid #e5e7eb",
                    width: w,
                    minWidth: w,
                  }}
                >
                  <Skeleton style={{ height: 12, width: "66%" }} />
                </th>
              ))}
              <th style={{ width: 36, minWidth: 36, borderRight: "1px solid #e5e7eb" }} />
              {GDS_NUM_COLUMNS.map((w, i) => (
                <th
                  key={i}
                  style={{
                    padding: "16px 12px",
                    borderRight: "1px solid #e5e7eb",
                    width: w,
                    minWidth: w,
                  }}
                >
                  <Skeleton style={{ height: 12, width: 48, marginLeft: "auto" }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: partNumbers }, (_, p) =>
              Array.from({ length: GDS_ROWS_PER_PN }, (_, l) => (
                <tr
                  key={`${p}-${l}`}
                  style={{
                    borderBottom: l === GDS_ROWS_PER_PN - 1 ? "2px solid #e5e7eb" : "1px solid #f3f4f6",
                  }}
                >
                  {l === 0 &&
                    GDS_TEXT_COLUMNS.map((_, i) => (
                      <td
                        key={i}
                        rowSpan={GDS_ROWS_PER_PN}
                        style={{
                          padding: "8px 12px",
                          verticalAlign: "top",
                          borderRight: "1px solid #e5e7eb",
                        }}
                      >
                        <Skeleton
                          style={{
                            height: 12,
                            width: TEXT_WIDTHS[(p + i) % TEXT_WIDTHS.length],
                          }}
                        />
                        {i === 1 && <Skeleton style={{ marginTop: 6, height: 12, width: "50%" }} />}
                      </td>
                    ))}
                  <td style={{ padding: "8px", borderRight: "1px solid #e5e7eb" }}>
                    <Skeleton style={{ height: 14, width: 14, margin: "0 auto" }} />
                  </td>
                  {GDS_NUM_COLUMNS.map((_, i) => (
                    <td key={i} style={{ padding: "8px 12px", borderRight: "1px solid #f3f4f6" }}>
                      <Skeleton style={{ height: 12, width: 48, marginLeft: "auto" }} />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationSkeleton />
    </div>
  );
};

/* ── Material Status ────────────────────────────────────────────────────── */

const LABEL_WIDTH = 268;
const COMPONENTS_WIDTH = 170;
const LOCKED_WIDTH = 76;
const WEEK_WIDTH = 76;
const MONTH_HEIGHT = 26;
const WEEK_HEIGHT = 32;
const LABEL_SKELETON_WIDTHS = ["112px", "80px", "128px", "96px"];

export const KpiSkeleton: React.FC = () => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        padding: "12px 16px",
        flex: 1,
        minWidth: "170px",
        boxSizing: "border-box",
      }}
    >
      <Skeleton style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
      <span style={{ minWidth: 0, flex: 1, display: "block" }}>
        <Skeleton style={{ width: 56, height: 22, borderRadius: 4 }} />
        <Skeleton style={{ marginTop: 4, width: 112, height: 12, borderRadius: 4 }} />
      </span>
    </div>
  );
};

export const BlockSkeleton: React.FC<{
  weeks: (number | string)[];
  showComponents?: boolean;
  showLocked?: boolean;
}> = ({ weeks, showComponents = true, showLocked = true }) => {
  const leftLocked = LABEL_WIDTH + (showComponents ? COMPONENTS_WIDTH : 0);

  return (
    <tbody style={{ borderBottom: "2px solid #e5e7eb" }} aria-hidden="true">
      <tr>
        <th
          style={{
            position: "sticky",
            left: 0,
            zIndex: 20,
            backgroundColor: "#f9fafb",
            padding: "8px",
            borderRight: "1px solid #d1d5db",
            borderTop: "1px solid #e5e7eb",
            borderBottom: "1px solid #e5e7eb",
            width: LABEL_WIDTH,
            minWidth: LABEL_WIDTH,
            textAlign: "left",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Skeleton style={{ width: 12, height: 12, flexShrink: 0 }} />
            <Skeleton style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0 }} />
            <Skeleton style={{ width: 64, height: 14 }} />
          </div>
          <Skeleton style={{ marginTop: 6, marginLeft: 18, width: 144, height: 10 }} />
        </th>
        {showComponents && (
          <td
            style={{
              position: "sticky",
              left: LABEL_WIDTH,
              zIndex: 20,
              backgroundColor: "#f9fafb",
              padding: "8px 12px",
              borderRight: "1px solid #e5e7eb",
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              width: COMPONENTS_WIDTH,
              minWidth: COMPONENTS_WIDTH,
              textAlign: "center",
              boxSizing: "border-box",
            }}
          >
            <Skeleton style={{ width: 96, height: 10, margin: "0 auto" }} />
          </td>
        )}
        {showLocked && (
          <td
            style={{
              position: "sticky",
              left: leftLocked,
              zIndex: 20,
              backgroundColor: "#f9fafb",
              padding: "8px",
              borderRight: "1px solid #e5e7eb",
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
              width: LOCKED_WIDTH,
              minWidth: LOCKED_WIDTH,
              textAlign: "center",
              boxSizing: "border-box",
            }}
          >
            <Skeleton style={{ width: 28, height: 10, margin: "0 auto" }} />
          </td>
        )}
        <td
          colSpan={weeks.length}
          style={{
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #e5e7eb",
            borderBottom: "1px solid #e5e7eb",
          }}
        />
      </tr>
      {LABEL_SKELETON_WIDTHS.map((width, idx) => (
        <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
          <td
            style={{
              position: "sticky",
              left: 0,
              zIndex: 10,
              backgroundColor: "#ffffff",
              padding: "6px 12px",
              borderRight: "1px solid #e5e7eb",
              width: LABEL_WIDTH,
              minWidth: LABEL_WIDTH,
              boxSizing: "border-box",
            }}
          >
            <Skeleton style={{ width, height: 10 }} />
            <Skeleton style={{ marginTop: 4, width: 48, height: 8 }} />
          </td>
          {showComponents && (
            <td
              style={{
                position: "sticky",
                left: LABEL_WIDTH,
                zIndex: 10,
                backgroundColor: "#ffffff",
                borderRight: "1px solid #e5e7eb",
                width: COMPONENTS_WIDTH,
                minWidth: COMPONENTS_WIDTH,
                boxSizing: "border-box",
              }}
            />
          )}
          {showLocked && (
            <td
              style={{
                position: "sticky",
                left: leftLocked,
                zIndex: 10,
                backgroundColor: "#ffffff",
                borderRight: "1px solid #e5e7eb",
                width: LOCKED_WIDTH,
                minWidth: LOCKED_WIDTH,
                boxSizing: "border-box",
              }}
            />
          )}
          {weeks.map((_, sIdx) => (
            <td
              key={sIdx}
              style={{
                padding: "4px 8px",
                borderRight: "1px solid #f3f4f6",
                textAlign: "right",
              }}
            >
              <Skeleton style={{ width: 36, height: 10, marginLeft: "auto" }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

export const FullGridSkeleton: React.FC<{
  weeks?: (number | string)[];
  blocks?: number;
  showComponents?: boolean;
  showLocked?: boolean;
}> = ({
  weeks = Array.from({ length: 24 }, (_, i) => i + 1),
  blocks = 5,
  showComponents = true,
  showLocked = true,
}) => {
  const totalWidth =
    LABEL_WIDTH +
    (showComponents ? COMPONENTS_WIDTH : 0) +
    (showLocked ? LOCKED_WIDTH : 0) +
    weeks.length * WEEK_WIDTH;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
      aria-busy="true"
      aria-label={translate.t("materialStatus.loading.grid", "Loading grid")}
    >
      <table style={{ borderCollapse: "collapse", fontSize: "14px", width: totalWidth }}>
        <thead>
          <tr>
            <th
              style={{
                backgroundColor: "#f3f4f6",
                borderRight: "1px solid #e5e7eb",
                padding: "0 12px",
                width: LABEL_WIDTH,
                minWidth: LABEL_WIDTH,
                height: MONTH_HEIGHT + WEEK_HEIGHT,
                textAlign: "left",
                boxSizing: "border-box",
              }}
            >
              <Skeleton style={{ height: 12, width: 112 }} />
            </th>
            {showComponents && (
              <th
                style={{
                  backgroundColor: "#f3f4f6",
                  borderRight: "1px solid #e5e7eb",
                  width: COMPONENTS_WIDTH,
                  minWidth: COMPONENTS_WIDTH,
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                <Skeleton style={{ height: 12, width: 64, margin: "0 auto" }} />
              </th>
            )}
            {showLocked && (
              <th
                style={{
                  backgroundColor: "#f3f4f6",
                  borderRight: "1px solid #e5e7eb",
                  width: LOCKED_WIDTH,
                  minWidth: LOCKED_WIDTH,
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                <Skeleton style={{ height: 12, width: 40, margin: "0 auto" }} />
              </th>
            )}
            {weeks.map((_, i) => (
              <th
                key={i}
                style={{
                  backgroundColor: "#f3f4f6",
                  borderRight: "1px solid #e5e7eb",
                  width: WEEK_WIDTH,
                  minWidth: WEEK_WIDTH,
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                <Skeleton style={{ height: 10, width: 48, margin: "0 auto" }} />
              </th>
            ))}
          </tr>
        </thead>
        {Array.from({ length: blocks }, (_, i) => (
          <BlockSkeleton
            key={i}
            weeks={weeks}
            showComponents={showComponents}
            showLocked={showLocked}
          />
        ))}
      </table>
    </div>
  );
};

export const MaterialStatusCardSkeleton: React.FC<{
  weeks?: (number | string)[];
  blocks?: number;
}> = ({
  weeks = Array.from({ length: 24 }, (_, i) => i + 1),
  blocks = 5,
}) => {
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        flex: 1,
        minHeight: "480px",
        boxSizing: "border-box",
        marginTop: "16px",
        marginBottom: "24px",
      }}
      aria-busy="true"
    >
      {/* Top filters bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "20px 20px 16px 20px",
          borderBottom: "1px solid #f3f4f6",
          flexWrap: "wrap",
        }}
      >
        <Skeleton style={{ height: 38, flex: 1, minWidth: 220, borderRadius: 8 }} />
        <Skeleton style={{ height: 38, width: 130, borderRadius: 8 }} />
        <Skeleton style={{ height: 38, width: 130, borderRadius: 8 }} />
        <Skeleton style={{ height: 16, width: 112, borderRadius: 4 }} />
        <Skeleton style={{ height: 38, width: 128, borderRadius: 8 }} />
      </div>

      {/* Grid controls bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "10px 20px",
          borderBottom: "1px solid #f3f4f6",
          flexWrap: "wrap",
        }}
      >
        <Skeleton style={{ height: 12, width: 80, borderRadius: 4 }} />
        <Skeleton style={{ height: 26, width: 170, borderRadius: 8 }} />
        <Skeleton style={{ height: 14, width: 192, borderRadius: 4 }} />
        <Skeleton style={{ height: 26, width: 110, borderRadius: 8, marginLeft: "auto" }} />
        <Skeleton style={{ height: 26, width: 92, borderRadius: 8 }} />
      </div>

      {/* Grid skeleton */}
      <FullGridSkeleton weeks={weeks} blocks={blocks} showComponents showLocked />

      {/* Pagination skeleton */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "16px",
          padding: "12px 20px",
          borderTop: "1px solid #f3f4f6",
        }}
      >
        <Skeleton style={{ height: 16, width: 96 }} />
        <Skeleton style={{ height: 24, width: 48 }} />
        <Skeleton style={{ height: 16, width: 80 }} />
        <Skeleton style={{ height: 24, width: 56 }} />
      </div>

      {/* Footer note skeleton */}
      <div style={{ borderTop: "1px solid #f3f4f6", padding: "10px 20px" }}>
        <Skeleton style={{ height: 12, width: 256 }} />
      </div>
    </div>
  );
};
